// Admin → Facilities Management
//
// Directory of external facilities (hospitals, clinics, collection points,
// labs) that the system needs to reference but does not operate. Onboarded
// doctors can be assigned to one of these so we know where an external
// referral originated. Facility codes are auto-generated from the name
// when the admin doesn't supply one.
//
// Visual: deep-black admin shell from the screenshot — gray-900 surface,
// faint borders, mono code chips, soft-tinted type pills. The look is
// intentionally consistent with Onboarded Doctors so the two pages read
// as a pair.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Stack, Typography, TextField, InputAdornment, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, CircularProgress, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  Tooltip, useTheme,
} from '@mui/material';
import {
  Apartment as FacilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

import {
  facilitiesListUrl, facilityCreateUrl,
  facilityUpdateUrl, facilityDeleteUrl,
} from '../BaseURL';

// ── Palette factory — picks the right surface/text tokens for the active
// theme mode. Accent colours stay the same in both modes so brand chips
// (Type pills, status badges, primary CTAs) read identically.
const paletteFor = (isDark) => ({
  isDark,
  pageBg:       isDark ? '#0B0D14' : '#F4F6FA',
  surface:      isDark ? '#11141E' : '#FFFFFF',
  surfaceAlt:   isDark ? '#161A26' : '#F8FAFC',
  fieldBg:      isDark ? '#0F121B' : '#FFFFFF',
  borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
  borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
  text:         isDark ? '#F5F7FB' : '#0F172A',
  textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.60)',
  textDim:      isDark ? 'rgba(255,255,255,0.40)' : 'rgba(15,23,42,0.40)',
  rowHover:     isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
  primary:      '#3B82F6',
  primaryStrong:'#2563EB',
  emerald:      '#22c55e',
  emeraldSoft:  isDark ? 'rgba(34,197,94,0.14)' : 'rgba(34,197,94,0.12)',
  amber:        '#f59e0b',
  danger:       '#ef4444',
  violet:       '#8b5cf6',
  muted:        isDark ? '#94a3b8' : '#64748b',
});

const TYPES = [
  { value: 'HOSPITAL',         label: 'Hospital' },
  { value: 'CLINIC',           label: 'Clinic' },
  { value: 'COLLECTION_POINT', label: 'Collection point' },
  { value: 'LABORATORY',       label: 'Laboratory' },
  { value: 'OTHER',            label: 'Other' },
];

const emptyForm = {
  id: null,
  name: '',
  code: '',
  type: 'COLLECTION_POINT',
  region: '',
  location: '',
  contact_phone: '',
  contact_email: '',
  is_active: true,
};

export default function FacilitiesManagement() {
  const muiTheme = useTheme();
  const T = useMemo(
    () => paletteFor(muiTheme.palette.mode === 'dark'),
    [muiTheme.palette.mode]
  );
  const sx = useMemo(() => buildSx(T), [T]);

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [me, setMe]       = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setMe(JSON.parse(raw));
    } catch (_) {}
  }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (!showInactive) params.activeOnly = 1;
      const { data } = await axios.get(facilitiesListUrl, { params, timeout: 12000 });
      setRows(data?.success ? (data.results || []) : []);
    } catch (err) {
      console.error('[facilities] list failed:', err.message);
      setRows([]);
      setSnack({ open: true, severity: 'error', message: 'Could not load facilities.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFacilities(); /* eslint-disable-next-line */ }, [showInactive]);

  // ── Derived list ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (typeFilter !== 'ALL' && r.type !== typeFilter) return false;
      if (!q) return true;
      return (
        String(r.name || '').toLowerCase().includes(q) ||
        String(r.code || '').toLowerCase().includes(q) ||
        String(r.location || '').toLowerCase().includes(q) ||
        String(r.contact_phone || '').toLowerCase().includes(q) ||
        String(r.contact_email || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, typeFilter]);

  // ── Dialog handlers ──────────────────────────────────────────────────
  const openCreate = () => { setForm(emptyForm); setDialogOpen(true); };
  const openEdit   = (row) => {
    setForm({
      id: row.id,
      name: row.name || '',
      code: row.code || '',
      type: row.type || 'COLLECTION_POINT',
      region: row.region || '',
      location: row.location || '',
      contact_phone: row.contact_phone || '',
      contact_email: row.contact_email || '',
      is_active: !!row.is_active,
    });
    setDialogOpen(true);
  };
  const closeDialog = () => { if (!saving) setDialogOpen(false); };

  const saveFacility = async () => {
    if (!form.name.trim()) {
      setSnack({ open: true, severity: 'error', message: 'Facility name is required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        type: form.type,
        region: form.region.trim() || null,
        location: form.location.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        currentUser: me?.username || me?.name || null,
        is_active: form.is_active,
      };
      if (form.id) {
        await axios.put(facilityUpdateUrl(form.id), payload, { timeout: 12000 });
      } else {
        await axios.post(facilityCreateUrl, payload, { timeout: 12000 });
      }
      setDialogOpen(false);
      setSnack({
        open: true, severity: 'success',
        message: form.id ? 'Facility updated.' : 'Facility added.',
      });
      fetchFacilities();
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not save facility.';
      setSnack({ open: true, severity: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const deleteFacility = async (row) => {
    const r = await Swal.fire({
      title: `Delete ${row.name}?`,
      html: `Doctors assigned to this facility will be unassigned (they fall back to <strong>Collection Point</strong>). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: T.danger,
      cancelButtonColor: T.borderStrong,
      background: T.surface,
      color: T.text,
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(facilityDeleteUrl(row.id), { timeout: 12000 });
      setSnack({ open: true, severity: 'success', message: 'Facility deleted.' });
      fetchFacilities();
    } catch (err) {
      const msg = err.response?.data?.message || 'Delete failed.';
      setSnack({ open: true, severity: 'error', message: msg });
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100%', bgcolor: T.pageBg, p: { xs: 1.5, md: 2.5 }, color: T.text }}>
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.75, md: 2.5 },
        mb: 2,
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
        display: 'flex', alignItems: 'center', gap: 1.75,
      }}>
        <Avatar sx={{
          bgcolor: 'rgba(59,130,246,0.16)',
          color: T.primary,
          width: 44, height: 44,
          border: `1px solid rgba(59,130,246,0.32)`,
        }}>
          <FacilityIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, color: T.text }}>
            Facilities Management
          </Typography>
          <Typography sx={{ color: T.textMuted, fontSize: '0.82rem', mt: 0.25 }}>
            Manage hospitals, clinics, and external facilities
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            textTransform: 'none', fontWeight: 700,
            color: '#fff',
            background: `linear-gradient(180deg, ${T.primary}, ${T.primaryStrong})`,
            px: 2, py: 1, borderRadius: 1.5,
            boxShadow: '0 8px 24px rgba(59,130,246,0.25)',
            '&:hover': { background: T.primaryStrong },
          }}
        >
          New Facility
        </Button>
      </Paper>

      {/* ── Filters strip ────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.5, md: 2 },
        mb: 2,
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search facilities..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: T.textDim, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={sx.field}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                sx={{ color: T.textMuted, '&.Mui-checked': { color: T.primary } }}
              />
            }
            label={<Typography sx={{ fontSize: '0.82rem', color: T.textMuted }}>Show inactive</Typography>}
            sx={{ minWidth: 130, m: 0 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={sx.select}
              MenuProps={{ PaperProps: { sx: sx.menuPaper } }}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              {TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* ── List ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, borderBottom: `1px solid ${T.borderSubtle}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: T.text }}>
            Facilities
          </Typography>
          <Typography sx={{ color: T.textMuted, fontSize: '0.78rem', mt: 0.25 }}>
            {filtered.length} {filtered.length === 1 ? 'facility' : 'facilities'} found
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: T.primary }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FacilityIcon sx={{ fontSize: 44, color: T.textDim, mb: 1 }} />
            <Typography sx={{ color: T.textMuted, fontWeight: 600 }}>
              {search || typeFilter !== 'ALL'
                ? 'No facilities match those filters.'
                : 'No facilities yet — click "New Facility" to add one.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Code', 'Type', 'Location', 'Contact', 'Doctors', 'Status', ''].map((h, i) => (
                    <TableCell key={h + i} sx={sx.headerCell}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(row => (
                  <TableRow key={row.id} sx={{
                    '&:hover': { bgcolor: T.rowHover },
                    borderBottom: `1px solid ${T.borderSubtle}`,
                  }}>
                    <TableCell sx={sx.cell}>
                      <Typography sx={{ fontWeight: 600, color: T.text }}>{row.name}</Typography>
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Typography sx={{
                        fontFamily: 'monospace', fontSize: '0.78rem', color: T.textMuted,
                      }}>
                        {row.code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Chip
                        size="small"
                        label={row.type || 'OTHER'}
                        sx={{
                          bgcolor: T.emeraldSoft,
                          color: T.emerald,
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          height: 22,
                          border: `1px solid rgba(34,197,94,0.32)`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      {(row.location || row.region) ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted, fontSize: '0.82rem' }}>
                          <LocationIcon sx={{ fontSize: 14 }} />
                          <span>
                            {[row.location, row.region].filter(Boolean).join(', ')}
                          </span>
                        </Stack>
                      ) : (
                        <Typography sx={{ color: T.textDim }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Stack spacing={0.5}>
                        {row.contact_phone && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted, fontSize: '0.78rem' }}>
                            <PhoneIcon sx={{ fontSize: 13 }} />
                            <span>{row.contact_phone}</span>
                          </Stack>
                        )}
                        {row.contact_email && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted, fontSize: '0.78rem' }}>
                            <EmailIcon sx={{ fontSize: 13 }} />
                            <span>{row.contact_email}</span>
                          </Stack>
                        )}
                        {!row.contact_phone && !row.contact_email && (
                          <Typography sx={{ color: T.textDim }}>—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted }}>
                        <PeopleIcon sx={{ fontSize: 15 }} />
                        <span>{row.doctor_count || 0}</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Chip
                        size="small"
                        label={row.is_active ? 'Active' : 'Inactive'}
                        sx={{
                          bgcolor: row.is_active ? 'rgba(34,197,94,0.14)' : (T.isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.06)'),
                          color: row.is_active ? T.emerald : T.muted,
                          fontWeight: 700, fontSize: '0.68rem', height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...sx.cell, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}
                          sx={{ color: T.textMuted, '&:hover': { color: T.primary } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteFacility(row)}
                          sx={{ color: T.danger, '&:hover': { bgcolor: 'rgba(239,68,68,0.10)' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Create / Edit dialog ─────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: T.surface, color: T.text, borderRadius: 2, border: `1px solid ${T.borderSubtle}` } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${T.borderSubtle}`, fontWeight: 800 }}>
          {form.id ? 'Edit facility' : 'New facility'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
          <Stack spacing={1.75} sx={{ mt: 1 }}>
            <TextField
              label="Facility name *"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={sx.dialogField}
              autoFocus
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Code (auto if blank)"
                size="small"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
              <FormControl size="small" sx={{ ...sx.dialogField, flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  MenuProps={{ PaperProps: { sx: sx.menuPaper } }}
                >
                  {TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Location"
                size="small"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
              <TextField
                label="Region"
                size="small"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Phone"
                size="small"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
              <TextField
                label="Email"
                size="small"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  sx={{ color: T.textMuted, '&.Mui-checked': { color: T.primary } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.85rem', color: T.text }}>Active</Typography>}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${T.borderSubtle}` }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: T.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={saveFacility}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(180deg, ${T.primary}, ${T.primaryStrong})`,
              px: 2.5, py: 0.8, borderRadius: 1.5,
              '&:hover': { background: T.primaryStrong },
            }}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            {form.id ? 'Save changes' : 'Add facility'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Shared sx factory — rebuilt per-render from the active palette so the
// page repaints correctly when the user flips theme mode.
const buildSx = (T) => ({
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: T.fieldBg,
      color: T.text,
      '& fieldset': { borderColor: T.borderSubtle },
      '&:hover fieldset': { borderColor: T.borderStrong },
      '&.Mui-focused fieldset': { borderColor: T.primary },
    },
    '& .MuiInputBase-input::placeholder': { color: T.textDim, opacity: 1 },
  },
  select: {
    borderRadius: 1.5,
    bgcolor: T.fieldBg,
    color: T.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.borderSubtle },
    '& .MuiSvgIcon-root': { color: T.textMuted },
  },
  menuPaper: {
    bgcolor: T.surfaceAlt,
    color: T.text,
    border: `1px solid ${T.borderSubtle}`,
    '& .MuiMenuItem-root:hover': { bgcolor: T.rowHover },
  },
  headerCell: {
    bgcolor: T.surface,
    color: T.textMuted,
    borderBottom: `1px solid ${T.borderSubtle}`,
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    py: 1.5,
  },
  cell: {
    color: T.text,
    borderColor: T.borderSubtle,
    py: 1.5,
    fontSize: '0.86rem',
  },
  dialogField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: T.fieldBg,
      color: T.text,
      '& fieldset': { borderColor: T.borderSubtle },
      '&:hover fieldset': { borderColor: T.borderStrong },
      '&.Mui-focused fieldset': { borderColor: T.primary },
    },
    '& .MuiInputLabel-root': { color: T.textMuted },
    '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
    '& .MuiSelect-icon': { color: T.textMuted },
  },
});
